import { BaseCall } from '../BaseCall'
import { APIConfig } from '../interfaces'

export type OAuthProjectWebAssignedUser = {
	userId: string
	email: string
	displayName: string
	createdAt: string
}

export type OAuthProjectWebSummary = {
	clientId: string
	name: string
	description: string | null
	redirectUris: string[]
	createdByUserId: string
	createdAt: string
	updatedAt: string | null
	iconUrl: string
	assignedUsers?: OAuthProjectWebAssignedUser[]
}

export class AdminOAuthProjects extends BaseCall {
	public list = async (): Promise<{ projects: OAuthProjectWebSummary[] }> => {
		const response = await this.client.get<{ projects: OAuthProjectWebSummary[] }>('/oauth-projects', { withCredentials: true })
		return response.data
	}

	public get = async (clientId: string): Promise<{ project: OAuthProjectWebSummary }> => {
		const response = await this.client.get<{ project: OAuthProjectWebSummary }>(`/oauth-projects/${encodeURIComponent(clientId)}`, {
			withCredentials: true
		})
		return response.data
	}

	public create = async (
		payload: {
			name: string
			description?: string
			redirectUris: string[]
			userIds?: string[]
			iconFile?: File
		}
	): Promise<{ project: OAuthProjectWebSummary; clientSecret: string }> => {
		if (payload.iconFile) {
			const formData = new FormData()
			formData.append('name', payload.name)
			if (payload.description) formData.append('description', payload.description)
			formData.append('redirectUris', JSON.stringify(payload.redirectUris))
			if (payload.userIds?.length) formData.append('userIds', JSON.stringify(payload.userIds))
			formData.append('icon', payload.iconFile)
			const response = await this.client.post<{ project: OAuthProjectWebSummary; clientSecret: string }>(
				'/oauth-projects',
				formData,
				{ withCredentials: true }
			)
			return response.data
		}

		const response = await this.client.post<{ project: OAuthProjectWebSummary; clientSecret: string }>('/oauth-projects', payload, {
			withCredentials: true
		})
		return response.data
	}

	public update = async (
		clientId: string,
		payload: { name: string; description?: string | null; redirectUris: string[] },
		iconFile?: File
	): Promise<{ project: OAuthProjectWebSummary }> => {
		if (iconFile) {
			const formData = new FormData()
			formData.append('name', payload.name)
			formData.append('description', payload.description ?? '')
			formData.append('redirectUris', JSON.stringify(payload.redirectUris))
			formData.append('icon', iconFile)
			const response = await this.client.patch<{ project: OAuthProjectWebSummary }>(`/oauth-projects/${encodeURIComponent(clientId)}`, formData, {
				withCredentials: true
			})
			return response.data
		}

		const response = await this.client.patch<{ project: OAuthProjectWebSummary }>(`/oauth-projects/${encodeURIComponent(clientId)}`, payload, {
			withCredentials: true
		})
		return response.data
	}

	public delete = async (clientId: string): Promise<void> => {
		await this.client.delete(`/oauth-projects/${encodeURIComponent(clientId)}`, { withCredentials: true })
	}

	public regenerateSecret = async (clientId: string): Promise<{ clientSecret: string }> => {
		const response = await this.client.post<{ clientSecret: string }>(
			`/oauth-projects/${encodeURIComponent(clientId)}/regenerate-secret`,
			{},
			{ withCredentials: true }
		)
		return response.data
	}

	public assignUser = async (clientId: string, userId: string): Promise<{ assignedUsers: OAuthProjectWebAssignedUser[] }> => {
		const response = await this.client.post<{ assignedUsers: OAuthProjectWebAssignedUser[] }>(
			`/oauth-projects/${encodeURIComponent(clientId)}/users`,
			{ userId },
			{ withCredentials: true }
		)
		return response.data
	}

	public removeUser = async (clientId: string, userId: string): Promise<{ assignedUsers: OAuthProjectWebAssignedUser[] }> => {
		const response = await this.client.delete<{ assignedUsers: OAuthProjectWebAssignedUser[] }>(
			`/oauth-projects/${encodeURIComponent(clientId)}/users/${encodeURIComponent(userId)}`,
			{ withCredentials: true }
		)
		return response.data
	}

	public setConfig(config: APIConfig): void {
		this.setBaseUrl(config.API_SERVER)
	}
}
